import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  AnalyticsPageName,
  BacktestingTimeframe,
  LanguageNameSpaces,
  TimeRange,
} from "@shared/types";
import {
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_3_MONTHS,
  TIME_RANGE_6_MONTHS,
} from "@shared/common/constants";
import usePeriodicStats from "@shared/features/bots/BotForm/Backtesting/usePeriodicStats";
import { useContext, useMemo, useState } from "react";
import { getNetPnlChange } from "@shared/features/newMarketplace/utils";
import { SubGroupDetailsContext } from "@shared/features/newMarketplace/SubGroupDetails.context";
import { toFixed } from "@shared/utils/numbers";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useOverviewStatisticsProps {
  isMobile?: boolean;
}

const useOverviewStatistics = ({ isMobile }: useOverviewStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { groupOverview, getGroupOverviewQuery } = useContext(
    SubGroupDetailsContext
  )!;
  const displayCurrency = useSelector(selectDisplayCurrency);

  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGE_6_MONTHS);
  const timeRanges = Object.keys(CHART_TIME_RANGE_TO_BACKTESTING_TIMEFRAME);

  const backtestingRun =
    groupOverview?.timeframe_to_backtesting_run_data?.[
      CHART_TIME_RANGE_TO_BACKTESTING_TIMEFRAME[timeRange]
    ];

  const { netPnlLineChart } = usePeriodicStats({
    backtestingRun,
    periodicStatsData: backtestingRun?.periodic_stats,
    numPoints: isMobile ? 50 : undefined,
    suffix: "%",
  });

  const netPnlPercentSeries = useMemo(() => {
    if (!backtestingRun) return [];
    const initialFunds = backtestingRun.initial_portfolio_size[displayCurrency];
    return netPnlLineChart.map((dataPoint) => {
      return {
        ...dataPoint,
        y: (dataPoint.y / initialFunds) * 100,
      };
    });
  }, [netPnlLineChart, backtestingRun, displayCurrency]);

  const netPnlPercentLineChart = useMemo(
    () =>
      netPnlPercentSeries.map((dataPoint) => ({
        ...dataPoint,
        y: toFixed(dataPoint.y, 2),
      })),
    [netPnlPercentSeries]
  );

  const lineChartData = {
    data: netPnlPercentLineChart,
    precision: 1,
    valueSuffix: "%",
  };

  const getPnlStatisticData = (timeframe: BacktestingTimeframe) => {
    return {
      title: `${timeframe / 30} month PnL`,
      change: getNetPnlChange(groupOverview, timeframe, displayCurrency),
    };
  };

  const graphTooltipProps = {
    description: t("overview.accumulated_net_profit_helper"),
    analyticsPageName: AnalyticsPageName.MARKETPLACE,
    analyticsFieldName: t("overview.accumulated_net_profit"),
  };

  return {
    displayCurrency,
    lineChartData,
    netPnlPercentSeries,
    timeRange,
    setTimeRange,
    timeRanges,
    getPnlStatisticData,
    groupOverview,
    getGroupOverviewQuery,
    graphTooltipProps,
  };
};

export default useOverviewStatistics;

const CHART_TIME_RANGE_TO_BACKTESTING_TIMEFRAME: Record<
  TimeRange,
  BacktestingTimeframe
> = {
  [TIME_RANGE_1_WEEK]: 7,
  [TIME_RANGE_1_MONTHS]: 30,
  [TIME_RANGE_3_MONTHS]: 90,
  [TIME_RANGE_6_MONTHS]: 180,
};
